import type { InstallRunner } from "./flow/runner";
import { STEP_ORDER, resumeHint, type Step } from "./flow/machine";
import { dir, setLocale, t } from "./i18n";
import type { RingLogger } from "./logs/ring";
import { EnrollStep } from "./ui/EnrollStep";
import { ErrorBanner } from "./ui/ErrorBanner";
import { FlashStep } from "./ui/FlashStep";
import { GroupStep } from "./ui/GroupStep";
import { HopTestStep } from "./ui/HopTestStep";
import { LiveStep } from "./ui/LiveStep";
import { LogsPanel } from "./ui/LogsPanel";
import { MemberStep } from "./ui/MemberStep";
import { PreflightStep } from "./ui/PreflightStep";
import { RemoveStep } from "./ui/RemoveStep";
import { SignInStep, useModels } from "./ui/SignInStep";
import { UnlockStep } from "./ui/UnlockStep";
import { useFlowState, useFocusOnStep, useLocale } from "./ui/hooks";
import { useEffect, useState } from "preact/hooks";

export function App({ runner, log }: { runner: InstallRunner; log: RingLogger }) {
  const s = useFlowState(runner.store);
  const l = useLocale();
  const models = useModels(runner);
  useFocusOnStep(s.step);
  useEffect(() => {
    document.documentElement.lang = l;
    document.documentElement.dir = dir(l);
    localStorage.setItem("qh-installer:lang", l);
  }, [l]);
  // A page load lands here with no USB grant; the device steps need a reconnect click.
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    const hint = resumeHint(runner.store.state);
    setConnected(hint === "fresh");
  }, [runner]);
  useEffect(() => runner.store.subscribe((st) => {
    if (st.step === "unlock" || st.step === "flash") setConnected(true);
    if (st.step === "preflight" && !st.device) setConnected(false);
    if (st.error?.code === "device_disconnected") setConnected(false);
  }), [runner]);

  const stepIndex = STEP_ORDER.indexOf(s.step);
  const reconnectOnly = s.step === "preflight" && !!s.device && !!s.preflight?.ok;

  let body;
  switch (s.step) {
    case "signin": body = <SignInStep runner={runner} models={models} />; break;
    case "group": body = <GroupStep runner={runner} />; break;
    case "preflight": body = <PreflightStep runner={runner} s={s} models={models} reconnectOnly={reconnectOnly} />; break;
    case "unlock": body = <UnlockStep runner={runner} s={s} connected={connected} />; break;
    case "flash": body = <FlashStep runner={runner} s={s} connected={connected} autoStart={connected && (s.flash?.phase ?? "manifest") === "manifest"} />; break;
    case "enroll": body = <EnrollStep runner={runner} s={s} />; break;
    case "member": body = <MemberStep runner={runner} s={s} />; break;
    case "hoptest": body = <HopTestStep runner={runner} s={s} />; break;
    case "remove": body = <RemoveStep runner={runner} s={s} />; break;
    case "live": body = <LiveStep runner={runner} s={s} />; break;
    default: body = null;
  }

  return (
    <div class="shell">
      <a class="skip-link" href="#main">{t("app.skip_to_content")}</a>
      <header class="top">
        <div class="brand">
          <h1>{t("app.title")}</h1>
          <small>{t("app.tagline")}</small>
        </div>
        <div class="actions">
          <button type="button" onClick={() => setLocale(l === "ar" ? "en" : "ar")} lang={l === "ar" ? "en" : "ar"} aria-label={t("app.lang.switch")}>{t("app.lang.switch")}</button>
          {s.business && <span class="muted">{s.business.name}</span>}
          {s.business && <button type="button" onClick={() => void runner.signOut()}>{t("app.signout")}</button>}
        </div>
      </header>
      <nav aria-label={t("app.status_region")}>
        <ol class="stepper">
          {STEP_ORDER.map((st: Step, i) => (
            <li key={st} class={i < stepIndex ? "done" : ""} aria-current={st === s.step ? "step" : undefined}>{t(`steps.${st}`)}</li>
          ))}
        </ol>
        <p class="sr-only" aria-live="polite">{stepIndex >= 0 ? t("steps.progress", { n: stepIndex + 1, total: STEP_ORDER.length, name: t(`steps.${s.step}`) }) : t(`steps.${s.step}`)}</p>
      </nav>
      <main id="main">
        {s.error && <ErrorBanner error={s.error} onRetry={() => runner.retry()} />}
        {body}
        {s.group && s.step !== "group" && s.step !== "signin" && (
          <p><button type="button" class="link" onClick={() => runner.restartWithAnotherPhone()}>{t("common.start_over")}</button></p>
        )}
      </main>
      <LogsPanel log={log} runner={runner} />
      <footer class="bottom">install.quickhub.ai · {t("logs.session", { id: s.sessionId })}</footer>
    </div>
  );
}
